import React, { useState } from 'react';

export interface DemoSection {
    /** DOM id of the element to scroll to. */
    id: string;
    title: string;
}

const navigatorStyle: React.CSSProperties = {
    position: 'fixed',
    right: '20px',
    top: '20px',
    zIndex: 1000,
    padding: '8px 12px',
    background: '#ffffff',
    border: '1px solid #b3b3b3',
    borderRadius: '8px',
    boxShadow: '0px 4px 8px #00000025',
    fontFamily: "'Inter', 'Helvetica Neue', 'Segoe UI', helvetica, verdana, sans-serif",
    fontSize: '13px',
    color: '#3c3c3c',
    cursor: 'pointer',
};

const backToTopValue = '__backToTop__';

/** Scrolls the window, and every scrollable container the page sits in, all the way to the top. */
const scrollToVeryTop = (pageElement?: HTMLElement | null) => {
    for (let el = pageElement?.parentElement; el; el = el.parentElement) {
        if (el.scrollTop > 0) {
            el.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

/**
 * Dropdown pinned to the top-right of the window that scrolls to a section of the demo page. "Back To Top" scrolls to
 * the very top: the window, plus any scrollable container around `topRef` (the demo page) when given.
 */
export const SectionNavigator: React.FC<{ sections: DemoSection[]; topRef?: React.RefObject<HTMLElement | null> }> = ({
    sections,
    topRef,
}) => {
    const [selectedId, set_selectedId] = useState('');

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (e.target.value === backToTopValue) {
            // back at the top, so show the placeholder again
            set_selectedId('');
            scrollToVeryTop(topRef?.current);
            return;
        }
        set_selectedId(e.target.value);
        document.getElementById(e.target.value)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <select
            aria-label='Jump to section'
            value={selectedId}
            onChange={handleChange}
            style={navigatorStyle}
        >
            <option
                value=''
                disabled
            >
                Jump to section…
            </option>
            <option value={backToTopValue}>Back To Top</option>
            {sections.map((section) => (
                <option
                    key={section.id}
                    value={section.id}
                >
                    {section.title}
                </option>
            ))}
        </select>
    );
};
